import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';
import { ForgotPasswordForm } from '../components/auth/ForgotPasswordForm';
import { useAuth } from '../contexts/AuthContext';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Sparkles, ArrowRight, CheckCircle, Zap, Shield, AlertTriangle, UserPlus } from 'lucide-react';

type AuthView = 'login' | 'register' | 'forgot-password';

export const AuthPage: React.FC = () => {
  const { error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for invitation flow
  const locationState = location.state as { returnTo?: string; invitationEmail?: string } | null;
  const invitationEmail = locationState?.invitationEmail;
  const returnTo = locationState?.returnTo;

  // Default to register view when coming from invitation (user likely needs an account)
  const [currentView, setCurrentView] = useState<AuthView>(invitationEmail ? 'register' : 'login');

  // Persist returnTo in localStorage so it survives Supabase email verification redirect
  useEffect(() => {
    if (returnTo) {
      localStorage.setItem('pendingInvitationReturn', returnTo);
    }
  }, [returnTo]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // If we have a return URL from invitation, go there
      if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, navigate, returnTo]);

  return (
    <div className="min-h-screen bg-gradient-surface animate-fade-in">
      <div className="flex min-h-screen">
        {/* Left Side - Branding & Features */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden animate-slide-left">
          {/* Background with gradient and pattern */}
          <div className="absolute inset-0 bg-gradient-primary opacity-90"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIxLjUiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          
          <div className="relative z-10 flex flex-col justify-center px-12 py-24 text-white">
            {/* Logo and Brand */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className="ml-3 text-3xl font-bold">TinyScale</span>
              </div>
              <h1 className="text-4xl font-bold leading-tight mb-4">
                Transform Your Product Images with AI
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                Sequential image processing for e-commerce that scales with your business
              </p>
            </div>

            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Lightning Fast Processing</h3>
                  <p className="text-white/70">Process thousands of images with our optimized pipeline</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Enterprise Security</h3>
                  <p className="text-white/70">Bank-grade encryption and secure cloud infrastructure</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Multi-Platform Integration</h3>
                  <p className="text-white/70">Magento, Shopify, WooCommerce and more coming soon</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Right Side - Auth Forms */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 animate-slide-right">
          <div className="w-full max-w-md">
            {/* Mobile Logo - only shown on small screens */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className="ml-3 text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent">TinyScale</span>
              </div>
              <p className="text-muted-foreground">
                Transform your product images with AI
              </p>
            </div>

            {/* Invitation Notice */}
            {invitationEmail && currentView !== 'forgot-password' && (
              <div className="mb-6 animate-bounce-in">
                <Alert className="shadow-lg border-primary/50 bg-primary/5">
                  <UserPlus className="h-4 w-4 text-primary" />
                  <AlertDescription>
                    <div className="space-y-1">
                      <p className="font-medium">You have a pending invitation!</p>
                      <p className="text-sm text-muted-foreground">
                        Please {currentView === 'login' ? 'log in' : 'register'} with <strong>{invitationEmail}</strong> to accept your invitation.
                      </p>
                    </div>
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Global Auth Message Alert */}
            {error && currentView !== 'forgot-password' && (
              <div className="mb-6 animate-bounce-in">
                {error.toLowerCase().includes('successfully') || error.toLowerCase().includes('created') ? (
                  // Success message - green styling
                  <Alert className="shadow-lg pt-4 pb-3 border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="flex items-center justify-between w-full translate-y-0 text-green-800 dark:text-green-200">
                      <span className="flex-1">{error}</span>
                      <button
                        onClick={clearError}
                        className="ml-4 text-xs hover:underline opacity-75 hover:opacity-100 whitespace-nowrap"
                      >
                        Dismiss
                      </button>
                    </AlertDescription>
                  </Alert>
                ) : (
                  // Error message - orange/warning styling
                  <Alert variant="warning" className="shadow-lg pt-4 pb-3">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between w-full translate-y-0">
                      <span className="flex-1">{error}</span>
                      <button
                        onClick={clearError}
                        className="ml-4 text-xs hover:underline opacity-75 hover:opacity-100 whitespace-nowrap"
                      >
                        Dismiss
                      </button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Auth Form Container */}
            <div className="animate-fade-in">
              {currentView === 'login' && (
                <LoginForm
                  onSwitchToRegister={() => setCurrentView('register')}
                  onForgotPassword={() => setCurrentView('forgot-password')}
                />
              )}
              {currentView === 'register' && (
                <RegisterForm onSwitchToLogin={() => setCurrentView('login')} defaultEmail={invitationEmail} />
              )}
              {currentView === 'forgot-password' && (
                <ForgotPasswordForm onBackToLogin={() => setCurrentView('login')} />
              )}
            </div>

            {/* Additional Info */}
            <div className="mt-8 text-center">
              <p className="text-xs text-muted-foreground">
                By signing up, you agree to our{' '}
                <a href="#" className="text-primary hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-primary hover:underline">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};